import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { AuthPage } from '@/features/auth';

import { useConfirmEmail } from '../hooks/use-account-mutations';

export interface ConfirmEmailPageProps {
  /** From the confirmation email sent to the new address. */
  token: string | undefined;
}

/** Target of the "confirm your new email" link; works signed in or out, the token is the proof. */
export function ConfirmEmailPage({ token }: ConfirmEmailPageProps) {
  const { t } = useTranslation('account');
  const confirm = useConfirmEmail();
  const started = useRef(false);
  const { mutate } = confirm;

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    mutate(token);
  }, [mutate, token]);

  const back = <Link to="/login">{t('confirmEmail.signIn')}</Link>;

  if (!token) {
    return (
      <AuthPage
        title={t('confirmEmail.invalidTitle')}
        description={t('confirmEmail.missing')}
        footer={back}
      />
    );
  }
  if (confirm.isError) {
    const invalid = isApiError(confirm.error) && confirm.error.kind === 'unauthorized';
    return (
      <AuthPage
        title={t('confirmEmail.invalidTitle')}
        description={invalid ? t('confirmEmail.invalid') : undefined}
        footer={back}
      >
        {invalid ? null : (
          <ErrorState error={confirm.error} onRetry={() => confirm.mutate(token)} />
        )}
      </AuthPage>
    );
  }
  if (confirm.isSuccess) {
    return (
      <AuthPage
        title={t('confirmEmail.doneTitle')}
        description={t('confirmEmail.done', { email: confirm.data.email })}
      >
        <Button asChild size="lg" className="h-11 w-full">
          <Link to="/org">{t('confirmEmail.continue')}</Link>
        </Button>
      </AuthPage>
    );
  }
  return (
    <AuthPage title={t('confirmEmail.checking')}>
      <Skeleton className="h-11" aria-busy />
    </AuthPage>
  );
}
